/** Кластеры складов: матчинг правил и сигнал по кластерам — порт warehouseCluster
 * (build-скрипт, стр. 4947-4962) и productionClusterSignal (стр. 6987-7075).
 * Правила — из БД портала; данные — bulk-агрегат /api/orders/warehouses-agg. */
import type { MpCode } from "@/api/orders";
import type { WarehouseClusterRule } from "@/api/orders-planning";

export interface ClusterRef {
  name: string;
  color: string;
}

export const NO_CLUSTER: ClusterRef = { name: "Без кластера", color: "#7f90a7" };

/** Порт normalizeWarehouseText: lower, ё→е, [_-]+ → пробел. */
export function normalizeWarehouseText(value: string): string {
  return String(value ?? "")
    .toLowerCase()
    .replace(/ё/g, "е")
    .replace(/[_-]+/g, " ");
}

interface ExpandedRule {
  mp: MpCode;
  name: string;
  color: string;
  patterns: string[];
}

/** Разворот правил: mp=all → по одному на каждый МП; паттерны нормализованы. */
export function expandRules(rules: WarehouseClusterRule[]): ExpandedRule[] {
  const result: ExpandedRule[] = [];
  const mps: MpCode[] = ["ozon", "wb", "ym"];
  for (const rule of rules) {
    const patterns = rule.patterns
      .split(/[,;\n]+/)
      .map((p) => normalizeWarehouseText(p).trim())
      .filter(Boolean);
    if (!patterns.length) continue;
    const targets = rule.mp === "all" ? mps : [rule.mp as MpCode];
    for (const mp of targets) {
      result.push({ mp, name: rule.name, color: rule.color, patterns });
    }
  }
  return result;
}

/** Первый совпавший по порядку кластер для склада МП (подстрочный матч). */
export function warehouseCluster(
  mp: string,
  warehouseName: string,
  expanded: ExpandedRule[],
): ClusterRef {
  const name = normalizeWarehouseText(warehouseName);
  for (const rule of expanded) {
    if (rule.mp !== mp) continue;
    if (rule.patterns.some((p) => name.includes(p))) {
      return { name: rule.name, color: rule.color };
    }
  }
  return NO_CLUSTER;
}

// ── Сигнал по кластерам для строки артикула ──

export interface WarehouseAggRow {
  article: string;
  mp_code: MpCode;
  warehouse_name: string;
  qty: number;
  total: number;
  stock_mp: number;
}

export type ClusterStatus = "urgent" | "risk" | "rebalance" | "surplus" | "ok";

export interface ClusterAgg {
  mp: MpCode;
  cluster: ClusterRef;
  sales: number;
  stock: number;
  warehouses: number;
  daily: number;
  stockDays: number | null;
  salesShare: number;
  stockShare: number;
  status: ClusterStatus;
  action: string;
}

export interface ClusterRowSignal {
  status: ClusterStatus | "muted";
  label: string;
  note: string;
  clusters: ClusterAgg[];
}

const ACTION: Record<ClusterStatus, string> = {
  urgent: "Допоставить",
  risk: "Проверить",
  rebalance: "Перераспределить",
  surplus: "Излишек",
  ok: "OK",
};

/** Порт productionClusterSignal: агрегат по кластерам + статус строки.
 * rows — bulk-строки ЭТОГО артикула; scope — «all» или конкретный МП вкладки. */
export function clusterRowSignal(
  rows: WarehouseAggRow[],
  expanded: ExpandedRule[],
  selectedDays: number,
  scope: "all" | MpCode,
): ClusterRowSignal {
  const byCluster = new Map<string, ClusterAgg>();
  for (const row of rows) {
    if (scope !== "all" && row.mp_code !== scope) continue;
    const cluster = warehouseCluster(row.mp_code, row.warehouse_name, expanded);
    const key = `${row.mp_code}::${cluster.name}`;
    let agg = byCluster.get(key);
    if (!agg) {
      agg = {
        mp: row.mp_code, cluster, sales: 0, stock: 0, warehouses: 0,
        daily: 0, stockDays: 0, salesShare: 0, stockShare: 0, status: "ok", action: "OK",
      };
      byCluster.set(key, agg);
    }
    agg.sales += row.qty;
    agg.stock += row.stock_mp;
    agg.warehouses += 1;
  }
  const clusters = [...byCluster.values()];
  if (!clusters.length) {
    return { status: "muted", label: "Нет данных", note: "", clusters: [] };
  }
  const totalSales = clusters.reduce((s, c) => s + c.sales, 0);
  const totalStock = clusters.reduce((s, c) => s + c.stock, 0);
  for (const c of clusters) {
    c.daily = c.sales > 0 ? c.sales / selectedDays : 0;
    c.stockDays = c.daily > 0 ? Math.round(c.stock / c.daily) : c.stock > 0 ? null : 0;
    c.salesShare = totalSales > 0 ? c.sales / totalSales : 0;
    c.stockShare = totalStock > 0 ? c.stock / totalStock : 0;
    if ((c.sales > 0 && c.stock <= 0) || (c.stockDays !== null && c.stockDays <= 7 && c.sales > 0)) {
      c.status = "urgent";
    } else if (c.stockDays !== null && c.stockDays <= 21 && c.sales > 0) {
      c.status = "risk";
    } else if (c.salesShare - c.stockShare > 0.15) {
      c.status = "rebalance";
    } else if (c.stockShare - c.salesShare > 0.25) {
      c.status = "surplus";
    } else {
      c.status = "ok";
    }
    c.action = ACTION[c.status];
  }
  clusters.sort((a, b) => b.sales - a.sales || b.stock - a.stock);

  const urgent = clusters.filter((c) => c.status === "urgent").length;
  const risk = clusters.filter((c) => c.status === "risk").length;
  const skew = clusters.some((c) => c.status === "rebalance" || c.status === "surplus");
  const note = clusters
    .slice(0, 2)
    .map((c) => `${c.cluster.name}: ${c.action}`)
    .join(" · ");
  if (urgent) return { status: "urgent", label: `Критично ${urgent}`, note, clusters };
  if (risk) return { status: "risk", label: `Риск ${risk}`, note, clusters };
  if (skew) return { status: "rebalance", label: "Перекос", note, clusters };
  return { status: "ok", label: "OK", note, clusters };
}
